import { useQuery } from '@tanstack/react-query'
import { CreditCard, Download, ExternalLink, FileText, Receipt, ScrollText } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { documents as api } from '@/api/endpoints'
import { Alert, EmptyState, Spinner } from '@/components/ui'
import { cn, formatDate } from '@/lib/utils'
import type { SchoolContract, SchoolInvoice, SchoolTerms } from '@/types/api'

/**
 * Dokumenty školy voči platforme — zmluvy a faktúry.
 *
 * Sú to jediné dáta v aplikácii, ktoré nepochádzajú z databázy školy, ale
 * z registra platformy. Škola ich nemôže meniť; toto je archív, nie evidencia.
 *
 * Sťahuje sa cez blob, nie cez `<a href>`: endpoint vyžaduje prihlásenie
 * a obyčajný odkaz by šiel bez cookie aj bez hlavičky — rovnako ako pri
 * exporte štatistík.
 */
export default function DocumentsTab() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({ queryKey: ['documents'], queryFn: api.list })
  const [busy, setBusy] = useState('')
  const cardReturn = new URLSearchParams(window.location.search).get('card')

  async function download(kind: 'contract' | 'invoice', id: number) {
    const key = `${kind}-${id}`
    setBusy(key)
    try {
      const { blob, filename } =
        kind === 'contract' ? await api.contractPdf(id) : await api.invoicePdf(id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setBusy('')
    }
  }

  if (isLoading || !data) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl space-y-8">
      {/* Návrat zo Stripe - uhradená sa faktúra označí podľa webhooku. */}
      {cardReturn === 'ok' ? <Alert variant="success">{t('billing.card.thanks')}</Alert> : null}
      {cardReturn === 'cancel' ? <Alert variant="info">{t('billing.card.cancelled')}</Alert> : null}

      <TermsSection terms={data.terms} />

      <section className="space-y-3">
        <header>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <FileText size={15} className="text-slate-400" />
            {t('documents.contracts')}
          </h2>
          <p className="text-xs text-slate-500">{t('documents.contractsHint')}</p>
        </header>

        {data.contracts.length === 0 ? (
          <EmptyState>{t('documents.noContracts')}</EmptyState>
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">{t('documents.number')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('documents.period')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('documents.state')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('documents.price')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((row) => (
                  <ContractRow
                    key={row.id}
                    row={row}
                    busy={busy === `contract-${row.id}`}
                    onDownload={() => download('contract', row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <header>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Receipt size={15} className="text-slate-400" />
            {t('documents.invoices')}
          </h2>
          <p className="text-xs text-slate-500">{t('documents.invoicesHint')}</p>
        </header>

        {data.invoices.length === 0 ? (
          <EmptyState>{t('documents.noInvoices')}</EmptyState>
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">{t('documents.number')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('documents.issued')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('documents.due')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('documents.state')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('documents.total')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((row) => (
                  <InvoiceRow
                    key={row.id}
                    row={row}
                    busy={busy === `invoice-${row.id}`}
                    onDownload={() => download('invoice', row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function ContractRow({
  row,
  busy,
  onDownload,
}: {
  row: SchoolContract
  busy: boolean
  onDownload: () => void
}) {
  const { t } = useTranslation()

  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2 font-medium">{row.number}</td>
      <td className="px-3 py-2 text-slate-600">
        {formatDate(row.period_from)} – {formatDate(row.period_to)}
      </td>
      <td className="px-3 py-2">
        <StateBadge
          tone={row.signed ? 'ok' : row.awaiting_signature ? 'wait' : 'muted'}
          label={row.state_label}
        />
        {row.signed && row.signed_at ? (
          <div className="mt-0.5 text-xs text-slate-400">
            {t('documents.signedBy', {
              name: row.signer_name,
              date: formatDate(row.signed_at),
            })}
          </div>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-slate-600">
        {row.price} {row.currency}
        <span className="text-slate-400">{t('documents.perYear')}</span>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        {/* Nepodpísaná zmluva má prednosť pred sťahovaním: podpísať sa dá
            len z tej stránky a odkaz sa inak hľadá v starom e-maile. */}
        {row.awaiting_signature && row.sign_url ? (
          <a
            href={row.sign_url}
            target="_blank"
            rel="noreferrer"
            className="mr-3 inline-flex items-center gap-1 text-brand-600 hover:underline"
          >
            <ExternalLink size={14} />
            {t('documents.sign')}
          </a>
        ) : null}
        {row.has_pdf ? (
          <button type="button" className="btn-secondary" onClick={onDownload} disabled={busy}>
            {busy ? <Spinner /> : <Download size={14} />}
            PDF
          </button>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
    </tr>
  )
}

function InvoiceRow({
  row,
  busy,
  onDownload,
}: {
  row: SchoolInvoice
  busy: boolean
  onDownload: () => void
}) {
  const { t } = useTranslation()

  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2">
        <div className="font-medium">{row.number}</div>
        <div className="text-xs text-slate-400">
          {t('documents.variableSymbol')}: {row.variable_symbol}
        </div>
      </td>
      <td className="px-3 py-2 text-slate-600">{formatDate(row.issued)}</td>
      <td className="px-3 py-2 text-slate-600">{formatDate(row.due)}</td>
      <td className="px-3 py-2">
        <StateBadge
          tone={row.paid_on ? 'ok' : row.overdue ? 'bad' : 'wait'}
          label={row.overdue ? t('documents.overdue') : row.state_label}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-slate-600">
        {row.total} {row.currency}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        <div className="flex justify-end gap-1">
          {row.card ? <PayInvoiceButton id={row.id} /> : null}
          {row.has_pdf ? (
            <button type="button" className="btn-secondary" onClick={onDownload} disabled={busy}>
              {busy ? <Spinner /> : <Download size={14} />}
              PDF
            </button>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </div>
      </td>
    </tr>
  )
}

/** Zaplatiť faktúru kartou - presmeruje na platobnú stránku Stripe. Uhradená
 *  sa faktúra označí podľa webhooku, nie po návrate. */
function PayInvoiceButton({ id }: { id: number }) {
  const { t } = useTranslation()
  const [paying, setPaying] = useState(false)

  async function pay() {
    setPaying(true)
    try {
      const { url } = await api.payInvoice(id)
      window.location.assign(url)
    } catch (err) {
      window.alert(apiErrorMessage(err))
      setPaying(false)
    }
  }

  return (
    <button type="button" className="btn-primary" onClick={pay} disabled={paying}>
      {paying ? <Spinner /> : <CreditCard size={14} />}
      {t('billing.card.payShort')}
    </button>
  )
}

/** Farebná značka stavu. Zdieľaný `Badge` berie farbu z číselníka; tu ide
 *  o tri pevné stavy, tak sú menami, nie hexom. */
function StateBadge({ tone, label }: { tone: 'ok' | 'wait' | 'bad' | 'muted'; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
        tone === 'ok' && 'bg-emerald-50 text-emerald-700',
        tone === 'wait' && 'bg-amber-50 text-amber-700',
        tone === 'bad' && 'bg-rose-50 text-rose-700',
        tone === 'muted' && 'bg-slate-100 text-slate-600',
      )}
    >
      {label}
    </span>
  )
}

/**
 * Obchodné podmienky: ktorá verzia platí, či ju škola potvrdila a čo všetko
 * kedy potvrdila.
 *
 * História nie je kozmetika. Keď sa o dva roky rieši, čo platilo vlani, je to
 * jediné miesto, kde sa to škola dočíta bez toho, aby písala prevádzkovateľovi.
 */
function TermsSection({ terms }: { terms: SchoolTerms }) {
  const { t } = useTranslation()

  if (terms.version === null) return null

  return (
    <section className="space-y-3">
      <header>
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <ScrollText size={15} className="text-slate-400" />
          {t('documents.terms')}
        </h2>
        <p className="text-xs text-slate-500">{t('documents.termsHint')}</p>
      </header>

      <div className="rounded-md border border-slate-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <div className="font-medium">
              {t('documents.termsVersion', { version: terms.version })}
              <span className="ml-2 font-normal text-slate-500">
                {t('documents.effectiveFrom', {
                  date: formatDate(terms.effective_from),
                })}
              </span>
            </div>
            <div className="mt-1">
              <StateBadge
                tone={terms.needs_acceptance ? 'wait' : 'ok'}
                label={
                  terms.needs_acceptance
                    ? t('documents.termsPending')
                    : t('documents.termsAccepted')
                }
              />
            </div>
          </div>

          <a
            href={terms.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
          >
            <ExternalLink size={14} />
            {t('documents.termsOpen')}
          </a>
        </div>

        {terms.accepted.length > 0 ? (
          <ul className="mt-4 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
            {terms.accepted.map((row) => (
              <li key={row.version}>
                {t('documents.termsAcceptedBy', {
                  version: row.version,
                  name: row.user_name || '—',
                  date: formatDate(row.accepted_at),
                })}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  )
}
